import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
  Link,
} from "@mui/material";
import { EmailOutlined, LockOutlined } from "@mui/icons-material";
import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import arivuLogo from "../../assets/logo/ArivuHeaderWhite.png";
import loginVisual from "../../assets/Login.png";
import { useAuth } from "../../authentication/AuthProvider";
import { usePendingUser } from "../../authentication/PendingUserContext";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import config from "../../config";

// --- Custom Components ---

const CustomStepper = ({ activeStep }) => {
  const steps = ["Login", "Verification", "Complete"];
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0, mt: 6, width: "100%" }}>
      {steps.map((label, index) => (
        <Box key={label} sx={{ display: "flex", alignItems: "center", flex: index < steps.length - 1 ? 1 : "none" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
            <Box sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: activeStep >= index ? "#1e293b" : "#e2e8f0",
              color: activeStep >= index ? "#fff" : "#64748b",
              fontSize: "0.85rem",
              fontWeight: 700,
              zIndex: 2,
              transition: "all 0.3s ease"
            }}>
              {index + 1}
            </Box>
            <Typography sx={{
              position: "absolute",
              top: 40,
              whiteSpace: "nowrap",
              fontSize: "0.75rem",
              fontWeight: activeStep === index ? 700 : 500,
              color: activeStep === index ? "#1e293b" : "#64748b"
            }}>
              {label}
            </Typography>
          </Box>
          {index < steps.length - 1 && (
            <Box sx={{
              height: "2px",
              flex: 1,
              bgcolor: activeStep > index ? "#1e293b" : "#e2e8f0",
              mx: 2,
              transition: "all 0.3s ease"
            }} />
          )}
        </Box>
      ))}
    </Box>
  );
};

const CustomTextField = ({ label, showForgot, ...props }) => {
  const { t } = useTranslation();
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: "#111827",
            fontSize: "0.875rem",
          }}
        >
          {label}
        </Typography>
        {showForgot && (
          <Link href="#" underline="hover" sx={{ color: "#2A6B5A", fontSize: "0.8rem", fontWeight: 600 }}>
            {t("Forgot Password?")}
          </Link>
        )}
      </Box>
      <TextField
        fullWidth
        variant="outlined"
        {...props}
        InputProps={{
          ...props.InputProps,
          sx: {
            borderRadius: "8px",
            backgroundColor: "#f9fafb !important",
            height: "44px",
            fontSize: "0.9rem",
            "& fieldset": {
              borderColor: "#e5e7eb",
            },
            "&:hover fieldset": {
              borderColor: "#d1d5db",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#2A6B5A",
              borderWidth: "1.5px",
            },
            "& input": {
              color: "#111827 !important",
              WebkitTextFillColor: "#111827 !important",
            },
            // Fix for browser autofill color (blue/pink background)
            "& input:-webkit-autofill": {
              WebkitBoxShadow: "0 0 0 100px #f9fafb inset !important",
              WebkitTextFillColor: "#111827 !important",
              transition: "background-color 5000s ease-in-out 0s",
            },
            ...props.InputProps?.sx
          }
        }}
      />
    </Box>
  );
};

function Login() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { setPendingUser, pendingUser, setTermsAccepted } = usePendingUser();
  const auth = useAuth();
  const { selectedLanguage, setSelectedLanguage, enqueueAlert } = useContextProvider();

  // Auth State
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);

  // --- Auth Logic ---
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;
    setLoading(true);
    setIsInvalid(false);

    // Sanitize old auth storage & cookies before submitting new login credentials
    try {
      localStorage.removeItem("user");
      localStorage.removeItem("termsAccepted");
      localStorage.removeItem("username");
      sessionStorage.clear();
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date(0).toUTCString() + ";path=/");
      });
    } catch (e) {
      console.warn("Auth sanitization before login failed:", e);
    }

    try {
      const response = await fetch(`${config.apiUrl}/authenticate_user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: username, password }),
        credentials: "include",
      });
      const data = await response.json();
      if (response.ok) {
        setShow2FA(true);
        setPendingUser(data.user);
      } else {
        setIsInvalid(true);
      }
    } catch (error) {
      setIsInvalid(true);
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (event) => {
    event.preventDefault();
    const code = twoFACode.join("");
    if (!code || code.length < 6) return;
    setLoading(true);
    try {
      // /verify_2fa has no backend route (see arivu-api/app.py) — every request 404s
      // and was silently falling through to a catch-all login. Disabled until the
      // backend actually implements 2FA verification.
      // const response = await fetch(`${config.apiUrl}/verify_2fa`, {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify({ user_id: username, code }),
      //   credentials: "include",
      // });
      // if (response.ok) {
      //   const data = await response.json();
      //   const userToLogin = data.user || pendingUser;
      //   if (userToLogin) {
      //     await auth.login(userToLogin);
      //     setPendingUser(null);
      //     setTermsAccepted(true);
      //     localStorage.setItem("termsAccepted", "true");
      //     localStorage.setItem("username", username);
      //     navigate("/home");
      //   }
      // } else {
      //   enqueueAlert({ message: t("Invalid or expired verification code"), severity: "error" });
      // }

      if (code === "000000" && pendingUser) {
        await auth.login(pendingUser);
        setPendingUser(null);
        setTermsAccepted(true);
        localStorage.setItem("termsAccepted", "true");
        localStorage.setItem("username", username);
        navigate("/home");
      } else {
        enqueueAlert({ message: t("Invalid or expired verification code"), severity: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, "");
    setTwoFACode(prev => {
      const newCode = [...prev];
      newCode[index] = value;
      return newCode;
    });
    if (value.length > 0 && index < 5) inputRefs.current[index + 1].focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && twoFACode[index] === "" && index > 0) inputRefs.current[index - 1].focus();
  };

  return (
    <Box sx={{ display: "flex", width: "100vw", height: "calc(100vh - 60px)", backgroundColor: "#fff", overflow: "hidden" }}>

      {/* LEFT SIDE: VISUAL */}
      <Box sx={{
        width: { xs: "0%", md: "50%" },
        flex: { xs: 0, md: "0 0 50%" },
        display: { xs: "none", md: "block" },
        position: "relative",
        backgroundImage: `url(${loginVisual})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}>
        <Box sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.3)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center", // Centers the block horizontally
          pt: "38vh",
          px: 8,
          pb: 8,
        }}>
          <Box sx={{ position: "absolute", top: 30, left: 30 }}>
            <img
              src={arivuLogo}
              alt="Logo"
              style={{
                height: "65px",
              }}
            />
          </Box>
          <Box sx={{ maxWidth: "600px", width: "100%", display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left" }}>
            <Typography variant="h2" sx={{ color: "#fff", fontWeight: 800, mb: 3, lineHeight: 1.15, fontSize: "3.5rem" }}>
              {t("Democratizing")} <span style={{ color: theme.palette.arivuLogocolor1 || "#659d33" }}>{t("Sustainability")}</span> {t("for Every Decision")}
            </Typography>
            <Typography variant="h6" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 400, maxWidth: "480px" }}>
              {t("Access intelligent sustainability insights across your value chain.")}
            </Typography>
          </Box>

          {/* Copyright text at the bottom center as requested */}
          <Typography sx={{
            position: "absolute",
            bottom: 30,
            left: "50%",
            transform: "translateX(-50%)",
            color: "rgba(255,255,255,0.5)",
            fontSize: "0.8rem",
            fontWeight: 500,
            whiteSpace: "nowrap"
          }}>
            {/* {t("© 2026 Arivu . All right Reserved.")} */}
          </Typography>
        </Box>
      </Box>

      {/* RIGHT SIDE: FORM */}
      <Box sx={{
        width: { xs: "100%", md: "50%" },
        flex: { xs: 1, md: "0 0 50%" },
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        pt: show2FA ? "38vh" : "calc(38vh - 145px)",
        px: { xs: 4, md: 8, lg: 10 },
        height: "100%",
        backgroundColor: "#fff",
        overflow: "hidden",
        position: "relative",
      }}>

        {/* Language Switcher */}
        {/* <Box sx={{ position: "absolute", top: 24, right: 24, display: "flex", alignItems: "center", gap: 0, border: "1.5px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
          {["en", "fr"].map((lang) => (
            <Box
              key={lang}
              onClick={() => setSelectedLanguage(lang)}
              sx={{
                px: 2,
                py: 0.6,
                fontSize: "0.8rem",
                fontWeight: 600,
                cursor: "pointer",
                textTransform: "uppercase",
                backgroundColor: selectedLanguage === lang ? "#2A6B5A" : "transparent",
                color: selectedLanguage === lang ? "#fff" : "#6b7280",
                transition: "all 0.15s ease",
                userSelect: "none",
                "&:hover": { backgroundColor: selectedLanguage === lang ? "#2A6B5A" : "#f3f4f6" },
              }}
            >
              {lang}
            </Box>
          ))}
        </Box> */}

        <Box sx={{ width: "100%", maxWidth: "400px", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography variant="h4" sx={{
            fontWeight: 700,
            mb: 1.5,
            color: "#111827",
            fontSize: "2.25rem", // Larger centered heading as in reference
            textAlign: "center",
            width: "100%"
          }}>
            {show2FA ? t("Verification") : t("Welcome")}
          </Typography>
          <Typography variant="body2" sx={{
            color: "#6b7280",
            mb: 8,
            fontSize: "1rem",
            textAlign: "center",
            width: "100%"
          }}>
            {show2FA ? t("Enter the code sent to your email.") : (
              <>
                {t("Sign in to")} <span style={{ color: theme.palette.arivuLogocolor1 || "#659d33", fontWeight: 700 }}>Arivu</span>
              </>
            )}
          </Typography>

          {!show2FA ? (
            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              <Box sx={{ mb: 4 }}> {/* Increased space between elements */}
                <CustomTextField
                  label={t("Username")}
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t("Enter your username")}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined sx={{ fontSize: 20, color: "#9ca3af" }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>

              <Box sx={{ mb: 6 }}> {/* Increased space between elements */}
                <CustomTextField
                  label={t("Password")}
                  showForgot
                  id="password"
                  type={passwordVisible ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ fontSize: 20, color: "#9ca3af" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setPasswordVisible(!passwordVisible)} edge="end" sx={{ color: "#6b7280" }}>
                          <FontAwesomeIcon icon={passwordVisible ? faEyeSlash : faEye} size="xs" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Box>

              {isInvalid && (
                <Typography variant="caption" sx={{ color: "error.main", mb: 2, display: "block" }}>
                  {t("Invalid email or password.")}
                </Typography>
              )}

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                sx={{
                  mt: 2,
                  py: 1.8,
                  borderRadius: "8px",
                  backgroundColor: "#2A6B5A",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "1rem",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#1e5144" },
                  transition: "all 0.2s"
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : t("Sign In")}
              </Button>

              {/* Bottom Policy Links as requested */}
              <Box sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                mt: 6,
                color: "#6b7280",
                fontSize: "0.85rem",
                fontWeight: 500
              }}>
                <Link href="#" underline="hover" sx={{ color: "inherit" }}>{t("Privacy Policy")}</Link>
                <Typography sx={{ fontSize: "inherit", opacity: 0.5 }}>•</Typography>
                <Link href="#" underline="hover" sx={{ color: "inherit" }}>{t("Terms of Use")}</Link>
                <Typography sx={{ fontSize: "inherit", opacity: 0.5 }}>•</Typography>
                <Link href="#" underline="hover" sx={{ color: "inherit" }}>{t("Contact Support")}</Link>
              </Box>
            </form>
          ) : (
            <form onSubmit={handle2FASubmit}>
              <Box sx={{ display: "flex", gap: 1.5, justifyContent: "space-between", mb: 5 }}>
                {twoFACode.map((digit, index) => (
                  <TextField
                    key={index}
                    inputRef={(el) => (inputRefs.current[index] = el)}
                    value={digit}
                    onChange={(e) => handleCodeChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    inputProps={{
                      maxLength: 1,
                      style: {
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "1.5rem",
                        color: "#111827",
                        WebkitTextFillColor: "#111827",
                        padding: 0 // Remove padding to avoid vertical shifting
                      }
                    }}
                    sx={{
                      width: "56px",
                      "& .MuiOutlinedInput-root": {
                        height: "56px",
                        borderRadius: "12px",
                        backgroundColor: "#f9fafb",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "& fieldset": { borderColor: "#e5e7eb" },
                        "&.Mui-focused fieldset": { borderColor: "#2A6B5A", borderWidth: "2px" }
                      }
                    }}
                  />
                ))}
              </Box>

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                sx={{ py: 1.5, borderRadius: "8px", backgroundColor: "#2A6B5A", color: "#fff", fontWeight: 600, textTransform: "none", "&:hover": { backgroundColor: "#1e5144" } }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : t("Verify Code")}
              </Button>
            </form>
          )}


        </Box>
      </Box>
    </Box>
  );
}

export default Login;
